import React, { useEffect, useState } from 'react';
import ViewWrapperWithCommandBar from '../../../Layout/ViewWrapperWithCommandBar';
import Wizard from '../../../Wizard/Wizard';
import { DefaultPalette, ICommandBarItemProps, ISelectableOption, Text } from '@fluentui/react';
import { titleCommandBarButtonStyles, defaultCommandBarButtonStyles } from '../../../../styles/button-styles';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../../common/constants';
import { DateTime } from 'luxon';
import TimeTaxSupportSystemClient from '../../../../system';
import MessageCourier from '../../../../lib/MessageCourier';
import { useTranslation } from 'react-i18next';
import SettlementPeriod from './Wizard/SettlementPeriod';
import { SettlementsTable } from './Wizard/SettlementsTable';
import GenerateSettlement from './Wizard/GenerateSettlement';
import Loading from '../../../Spinners/Loading';
import { User } from '../../../../system/User';
import { LiquidacionInsumo } from '../../../../system/settlement/aje/liquidacion-insumos';
import {
  AgrupadorCorporacionLiquidadora,
  AgrupadorCorporacionLiquidadoraTipo,
} from '../../../../system/agrupador_corporacion_liquidadora/AgrupadorCorporacionLiquidadoraSystem';
import useAgrupadorCorporacionLiquidadoraList from '../../../../shared/hooks/useAgrupadorCorporacionLiquidadoraList';
import { LiquidacionAsincronaV2, LiquidacionAsincronaV2CreatePayload } from '../../../../system/liquidacion_asincrona/LiquidacionAsincronaV2System';
import { TipoInsumos } from '../../carga/enums/tipo_insumos.enum';
import { SettlementsDerivation } from './Wizard/SettlementsDerivation';
import { SettlementsGastosBolsita } from './Wizard/SettlementsGastosBolsita';

interface ISettlementsViewAjeProps {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: any;
  user: User;
}

export const DISTRIBUCION_PROYECTOS = 'P';
export const DISTRIBUCION_ACTIVIDADES = 'A';
export const DISTRIBUCION_HIBRIDA = 'H';

const DirectSettlementsViewAje = (props: ISettlementsViewAjeProps) => {
  const { t } = useTranslation();
  const [canGoNext, setCanGoNext] = useState(false);

  const [month, setMonth] = useState<DateTime>(DateTime.now());

  const [liquidacionInsumosList, setLiquidacionInsumosList] = useState<LiquidacionInsumo[]>([]);

  const [generatedSettlementResponse, setGeneratedSettlementResponse] = useState<LiquidacionAsincronaV2>();

  const [lastConfirmatedSettlement, setLastConfirmatedSettlement] = useState<String>();

  const [settlementType, setSettlementType] = useState<ISelectableOption<AgrupadorCorporacionLiquidadora>>({ key: '', text: '' });
  const agrupadoresCorporacionLiquidadora = useAgrupadorCorporacionLiquidadoraList(props.system);
  const [loadingInsumos, setLoadingInsumos] = useState(false);

  const navigate = useNavigate();

  const showDerivationStep = settlementType?.data?.agrcorliq_tipo === AgrupadorCorporacionLiquidadoraTipo.ASESORIAS;

  useEffect(() => {
    if (month && settlementType && settlementType.key) {
      getPreliquidacionReport(true);
    }
  }, [month, settlementType]);

  useEffect(() => {
    setLiquidacionInsumosList([]);
  }, [settlementType]);

  const generateSettlement = async () => {
    if (month) {
      props.changeBlockUI(true);
      try {
        const periodo = month.toFormat('yyyyLL');

        if (!settlementType || !settlementType.data) {
          props.courier.messageError(t('Debe seleccionar un tipo de liquidación antes de continuar'));
          return;
        }

        // Validar que el tipo de liquidación seleccionado sea compatible para ser generado como directa (AJE_FILMS)
        if (settlementType.data.agrcorliq_tipo === AgrupadorCorporacionLiquidadoraTipo.AJE_FILMS) {
          const insumoSubDiario = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.SUB_DIARIO);
          const horasProyectosAjefilmsDetallado = liquidacionInsumosList.find(
            (i) => i.inst_codigo === TipoInsumos.HORAS_PROYECTOS_AJEFILMS_DETALLADO,
          );
          const horasProyectosAjefilmsResumen = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.HORAS_PROYECTOS_AJEFILMS_RESUMEN);
          const horasProyectosAjefilmsResumenXlsx = liquidacionInsumosList.find(
            (i) => i.inst_codigo === TipoInsumos.HORAS_PROYECTOS_AJEFILMS_RESUMEN_XLSX,
          );

          if (
            [insumoSubDiario, horasProyectosAjefilmsDetallado, horasProyectosAjefilmsResumen, horasProyectosAjefilmsResumenXlsx].includes(undefined)
          ) {
            return props.courier.messageError(t('No se encontraron todos los insumos necesarios para generar la liquidación'));
          }

          const payload: LiquidacionAsincronaV2CreatePayload = {
            liquidacion: {
              lidasinc_agrcorliqid: settlementType.key,
              lidasinc_solicitud_colid: props.user.getCollaboratorId(),
              lidasinc_periodo: periodo,
            },
            detalle: {
              liqasincdet_data: {
                landing: [
                  {
                    ajeFilms: insumoSubDiario!.liqins_path,
                    horas_proyectos_ajefilms_detallado: horasProyectosAjefilmsDetallado!.liqins_path,
                    horas_proyectos_ajefilms_resumen: horasProyectosAjefilmsResumen!.liqins_path,
                    horas_proyectos_ajefilms_resumen_xlsx: horasProyectosAjefilmsResumenXlsx!.liqins_path,
                  },
                ],
              },
            },
          };
          const response = await props.system.getLiquidacionAsincronaV2System().create(payload);

          if (response && Object.keys(response.data).length > 1) {
            setGeneratedSettlementResponse(response.data);
            props.courier.messageSuccess(t('Se iniciará con el proceso de liquidación'));
            setButtonDisability(false);
          }
        } else if (settlementType.data.agrcorliq_tipo === AgrupadorCorporacionLiquidadoraTipo.ASESORIAS) {
          // Todos estos insumos si o si deben estar presentes
          const insumoSubDiario = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.SUB_DIARIO);
          //const insumoCC = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.CC);
          const insumoPYG = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.PYG);
          const insumoVentas = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.VENTAS);
          // const insumoHorasHnos = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.HORAS_HNOS);
          // const insumoHorasPeru = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.HORAS_PERU);
          const insumoPygIfrsEs = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.PYG_IFRS_ES);
          // const insumoHorasEspana = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.HORAS_ESPANA);
          // const insumoHorasAjefilms = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.HORAS_AJEFILMS);
          const insumoSubDiarioEspana = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.SUBDIARIO_ESPANA);
          const insumoOtrosGastosHoras = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.OTROS_GASTOS_HORAS);
          const insumoOtrosGastosFacturacion = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.OTROS_GASTOS_FACTURACION);
          const insumoDistribucionManualGastosBolsita = liquidacionInsumosList.find(
            (i) => i.inst_codigo === TipoInsumos.DISTRIBUCION_MANUAL_GASTOS_BOLSITA,
          );
          const insumoPlantillaDerivacionCostos = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.PLANTILLA_DERIVACION_COSTOS);

          if ([insumoDistribucionManualGastosBolsita, insumoPlantillaDerivacionCostos].some((item) => item == null)) {
            return props.courier.messageError(t('No se encontraron 2 insumos los insumos necesarios para generar la liquidación'));
          }

          // Copiar las plantillas y guardar en los insumos DISTRIBUCION_MANUAL_GASTOS_BOLSITA y PLANTILLA_DERIVACION_COSTOS
          const [insumoDistribucionManualGastosBolsitaData, insumoPlantillaDerivacionCostosData] = await Promise.all([
            props.system
              .getSettlementSystem()
              .copiarPlantillasEnInsumos(
                month.toFormat('yyyy-MM'),
                settlementType.key,
                insumoDistribucionManualGastosBolsita!.inst_id,
                TipoInsumos.DISTRIBUCION_MANUAL_GASTOS_BOLSITA,
              ),
            props.system
              .getSettlementSystem()
              .copiarPlantillasEnInsumos(
                month.toFormat('yyyy-MM'),
                settlementType.key,
                insumoPlantillaDerivacionCostos!.inst_id,
                TipoInsumos.PLANTILLA_DERIVACION_COSTOS,
              ),
          ]);

          // Validar que existan todo los insumos

          const horasAsesoriasPeruResumen = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_PERU_RESUMEN);
          const horasAsesoriasPeruDetallado = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_PERU_DETALLADO);
          const horasAsesoriasPeruResumenXlsx = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_PERU_RESUMEN_XLSX);
          const horasAsesoriasEspanaResumen = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_ESPANA_RESUMEN);
          const horasAsesoriasEspanaDetallado = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_ESPANA_DETALLADO);
          const horasAsesoriasEspanaResumenXlsx = liquidacionInsumosList.find(
            (i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_ESPANA_RESUMEN_XLSX,
          );
          const horasAsesoriasAjefilmsDetallado = liquidacionInsumosList.find(
            (i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_AJEFILMS_DETALLADO,
          );
          const horasAsesoriasAjefilmsResumen = liquidacionInsumosList.find((i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_AJEFILMS_RESUMEN);
          const horasAsesoriasAjefilmsResumenXlsx = liquidacionInsumosList.find(
            (i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_AJEFILMS_RESUMEN_XLSX,
          );
          if (
            [
              insumoSubDiario,
              //insumoCC,
              insumoPYG,
              insumoVentas,
              // insumoHorasHnos,
              // insumoHorasPeru,
              insumoPygIfrsEs,
              // insumoHorasEspana,
              // insumoHorasAjefilms, no validamos este insumo porque solo se usa en desarrollo
              insumoSubDiarioEspana,
              insumoOtrosGastosHoras,
              insumoOtrosGastosFacturacion,
              insumoDistribucionManualGastosBolsita,
              insumoPlantillaDerivacionCostosData,
              horasAsesoriasPeruResumen,
              horasAsesoriasPeruDetallado,
              horasAsesoriasPeruResumenXlsx,
              horasAsesoriasEspanaResumen,
              horasAsesoriasEspanaDetallado,
              horasAsesoriasEspanaResumenXlsx,
              horasAsesoriasAjefilmsDetallado,
              horasAsesoriasAjefilmsResumen,
              horasAsesoriasAjefilmsResumenXlsx,
            ].includes(undefined)
          ) {
            return props.courier.messageError(t('No se encontraron todos los insumos necesarios para generar la liquidacion'));
          }

          // Obtener el insumo de la liquidacion de aje films aceptada para ese periodo
          const liquidacionAjeFilmsPath = await props.system.getLiquidacionAsincronaV2System().getLiquidacionAjeFilmsPath(month);
          if (!liquidacionAjeFilmsPath)
            return props.courier.messageError(t('No se encontro la liquidacion de aje films para ese periodo seleccionado.'));

          const payload: LiquidacionAsincronaV2CreatePayload = {
            liquidacion: {
              lidasinc_agrcorliqid: settlementType.key,
              lidasinc_solicitud_colid: props.user.getCollaboratorId(),
              lidasinc_periodo: periodo,
            },
            detalle: {
              liqasincdet_data: {
                landing: [
                  {
                    //cc: insumoCC!.liqins_path,
                    pyg: insumoPYG!.liqins_path,
                    ventas: insumoVentas!.liqins_path,
                    subdiario: insumoSubDiario!.liqins_path,
                    // horas_hnos: insumoHorasHnos!.liqins_path,
                    // horas_peru: insumoHorasPeru!.liqins_path,
                    pyg_ifrs_es: insumoPygIfrsEs!.liqins_path,
                    // horas_espana: insumoHorasEspana!.liqins_path,

                    // Si existe el insumo se envia la key horas_ajefilms
                    // ...(insumoHorasAjefilms ? { horas_ajefilms: insumoHorasAjefilms!.liqins_path } : {}),

                    subdiario_espana: insumoSubDiarioEspana!.liqins_path,
                    otros_gastos_horas: insumoOtrosGastosHoras!.liqins_path,
                    otros_gastos_facturacion: insumoOtrosGastosFacturacion!.liqins_path,

                    distribucion_manual_gastos_bolsita: insumoDistribucionManualGastosBolsitaData!.liqins_path,
                    plantilla_derivacion_costos: insumoPlantillaDerivacionCostosData!.liqins_path,

                    reporte_detallado_horas_ajefilms: liquidacionAjeFilmsPath,

                    horas_asesorias_peru_resumen: horasAsesoriasPeruResumen!.liqins_path,
                    horas_asesorias_peru_detallado: horasAsesoriasPeruDetallado!.liqins_path,
                    horas_asesorias_peru_resumen_xlsx: horasAsesoriasPeruResumenXlsx!.liqins_path,

                    horas_asesorias_espana_resumen: horasAsesoriasEspanaResumen!.liqins_path,
                    horas_asesorias_espana_detallado: horasAsesoriasEspanaDetallado!.liqins_path,
                    horas_asesorias_espana_resumen_xlsx: horasAsesoriasEspanaResumenXlsx!.liqins_path,

                    horas_asesorias_ajefilms_detallado: horasAsesoriasAjefilmsDetallado!.liqins_path,
                    horas_asesorias_ajefilms_resumen: horasAsesoriasAjefilmsResumen!.liqins_path,
                    horas_asesorias_ajefilms_resumen_xlsx: horasAsesoriasAjefilmsResumenXlsx!.liqins_path,
                  },
                ],
              },
            },
          };
          const response = await props.system.getLiquidacionAsincronaV2System().create(payload);
          if (response && Object.keys(response.data).length > 1) {
            setGeneratedSettlementResponse(response.data);
            props.courier.messageSuccess(t('Se iniciará con el proceso de liquidacion'));
            setButtonDisability(true);
          }
        } else {
          props.courier.messageError(t('El tipo de liquidacion seleccionado no es compatible para ser generado'));
        }
      } catch (error) {
        props.courier.messageError(error);
      } finally {
        props.changeBlockUI(false);
      }
    }
  };

  const getPreliquidacionReport = async (showChangeBlockUI: boolean) => {
    if (month) {
      if (!settlementType || !settlementType.key) {
        props.courier.messageError(t('Debe seleccionar un tipo de liquidacion antes de continuar'));
        return;
      }

      if (showChangeBlockUI) props.changeBlockUI(true);
      setLoadingInsumos(true);
      props.courier.messageError('');
      try {
        setLiquidacionInsumosList([]);

        const periodo = month.toFormat('yyyyLL');
        const periodoHyphenated = month.toFormat('yyyy-LL');

        const existsResponse = await props.system.getLiquidacionAsincronaV2System().existsByPeriodo({
          lidasinc_periodo: periodo,
          lidasinc_agrcorliqid: settlementType.key,
        });

        if (existsResponse.data.existe) {
          props.courier.messageError(t('Ya existe una liquidación generada para el período seleccionado'));
          return;
        }

        const data = await props.system.getSettlementSystem().getInsumosByPeriodoAndAgrupador(periodoHyphenated, settlementType.key);

        setLiquidacionInsumosList(data);

        if (data.length === 0) {
          props.courier.messageError(t('No se encontraron insumos para ese periodo y grupo seleccionado'));
        } else {
          props.courier.messageError('');
        }
      } catch (error: any) {
        props.courier.messageError(error?.message || error);
      } finally {
        if (showChangeBlockUI) props.changeBlockUI(false);
        setLoadingInsumos(false);
      }
    }
  };

  const periodString = () => {
    return month.toFormat('LL/yyyy');
  };

  const Titles = [
    t('1. Período'),
    ...(showDerivationStep ? [t('2. Derivación de costos'), t('3. Gastos de bolsita')] : []),
    t(showDerivationStep ? '4. Validación de insumos' : '2. Validación de insumos'),
    t(showDerivationStep ? '5. Generar liquidación' : '3. Generar liquidación'),
  ];

  const Descriptions = [
    t('Elije el periodo y el tipo de liquidación correspondientes para generar la próxima liquidación directa.'),

    ...(showDerivationStep
      ? [
          t('En esta sección podrós revisar y modificar la plantilla de derivación de costos.'),
          t('En esta sección podrós revisar y modificar la plantilla de distribución manual de gastos de bolsita.'),
        ]
      : []),

    <div className="ms-Grid-row" style={{ maxWidth: 400 }}>
      <div className="ms-Grid-col ms-lg12 ms-sm12 mt-1">
        <div className="tts-liquidacion-info">
          <div className="tts-liquidacion-item">
            <span className="tts-liquidacion-label">{t('Periodo a liquidar')}:</span>
            <span className="tts-liquidacion-value">{periodString()}</span>
          </div>

          <div className="tts-liquidacion-item">
            <span className="tts-liquidacion-label">{t('Tipo de liquidación')}:</span>
            <span className="tts-liquidacion-value">{settlementType.text}</span>
          </div>
        </div>
      </div>
    </div>,

    t('Generar liquidación.'),
  ];

  const setButtonDisability = (can: boolean) => {
    setCanGoNext(can);
  };

  const itemsMenu = React.useMemo(
    (): ICommandBarItemProps[] => [
      {
        key: 'title',
        text: t('Liquidaciones directas'),
        iconProps: {
          iconName: 'TableComputed',
          color: DefaultPalette.themeLighterAlt,
          className: 'text-title-btn',
        },
        buttonStyles: titleCommandBarButtonStyles,
        className: 'text-title-btn',
      },
      {
        key: 'back',
        text: t('Volver'),
        onClick: () => navigate(`${routes.direct_settlement_list_aje}`),
        iconProps: { iconName: 'Back' },
        buttonStyles: defaultCommandBarButtonStyles,
      },
    ],
    [navigate],
  );

  const getDataFromCompanySelectorAssistant = (month: DateTime) => {
    setMonth(month);
  };

  const hasSettlementItems = () => {
    if (liquidacionInsumosList.length > 0) {
      return true;
    } else if (liquidacionInsumosList.length < 1) {
      return false;
    } else {
      return null;
    }
  };

  const Selectors = [
    <SettlementPeriod
      system={props.system}
      courier={props.courier}
      getDataFromCompanySelectorAssistant={getDataFromCompanySelectorAssistant}
      setButtonDisability={setButtonDisability}
      liquidacionInsumosList={liquidacionInsumosList}
      hasItems={hasSettlementItems()}
      month={month}
      lastSettlement={lastConfirmatedSettlement}
      setSettlementType={setSettlementType}
      settlementType={settlementType}
      agrupadoresCorporacionLiquidadora={agrupadoresCorporacionLiquidadora}
    />,
    ...(showDerivationStep
      ? [
          <SettlementsDerivation
            system={props.system}
            courier={props.courier}
            changeBlockUI={props.changeBlockUI}
            from={month}
            settlementType={settlementType}
            user={props.user}
            setButtonDisability={setButtonDisability}
            liquidacionInsumosList={liquidacionInsumosList}
          />,
          <SettlementsGastosBolsita
            system={props.system}
            courier={props.courier}
            changeBlockUI={props.changeBlockUI}
            from={month}
            settlementType={settlementType}
            user={props.user}
            setButtonDisability={setButtonDisability}
            liquidacionInsumosList={liquidacionInsumosList}
          />,
        ]
      : []),
    <SettlementsTable
      system={props.system}
      courier={props.courier}
      changeBlockUI={props.changeBlockUI}
      items={liquidacionInsumosList}
      setItems={setLiquidacionInsumosList}
      from={month}
      settlementType={settlementType}
      user={props.user}
      setButtonDisability={setButtonDisability}
      getPreliquidacionReport={getPreliquidacionReport}
      loading={loadingInsumos}
    />,
    <GenerateSettlement
      system={props.system}
      courier={props.courier}
      setButtonDisability={setButtonDisability}
      dataTable={liquidacionInsumosList}
      generateSettlement={generateSettlement}
      generatedSettlementResponse={generatedSettlementResponse}
      settlementType={settlementType}
      from={month}
      user={props.user}
    />,
  ];

  if (agrupadoresCorporacionLiquidadora.loading) {
    return <Loading title="Cargando" description="Por favor espere estamos procesando la información necesaria para completar su solicitud." />;
  }

  return (
    <ViewWrapperWithCommandBar
      withBorderBottom
      title={''}
      items={itemsMenu}
      overflowButtonStyles={defaultCommandBarButtonStyles}
      variant="highligth"
      titleBody={t('Proyectos')}
    >
      <div className="padding-1">
        <div className="ms-Grid bg-white-h100" dir="ltr">
          <div className="ms-Grid-row">
            <div className="ms-Grid-col ms-sm12 mt-2 ml-1 defense-file-container-desc">
              <Text className="ml-10px defense-file-title d-block">{t('Asistente para la generación de liquidaciones de servicios (directas)')}</Text>
            </div>
          </div>

          <div className="ms-Grid-row">
            <div className="ms-Grid-col ms-sm12 ml-1"></div>
          </div>
          <div className="ms-Grid-row">
            <div className="ms-Grid-col ms-sm12" style={{ overflow: 'auto' }}>
              <Wizard
                componentsTitlesArray={Titles}
                componentsDescriptions={Descriptions}
                componentsArray={Selectors}
                canGoNext={canGoNext}
                hiddenButtons={!!generatedSettlementResponse}
                disableStepNavigation={!!generatedSettlementResponse}
              />
            </div>
          </div>
        </div>
      </div>
    </ViewWrapperWithCommandBar>
  );
};

export default DirectSettlementsViewAje;
